import { defaultLocale, type Locale, supportedLocales } from "./config";

/** Type guard: is the string a supported locale? */
export function isValidLocale(value: string): value is Locale {
	return (supportedLocales as readonly string[]).includes(value);
}

/**
 * Extract the highest-priority SUPPORTED locale explicitly named in an
 * Accept-Language header. Returns `null` (never a fallback) when the header
 * is absent or names nothing we support — callers that need to distinguish
 * "the visitor's browser explicitly asked for `id`/`en`" from "we have no
 * signal, apply a default" (e.g. the locale-detection middleware, LD-7) must
 * use this rather than `getLocaleFromAcceptLanguage`, whose `defaultLocale`
 * fallback would otherwise conflate the two cases.
 */
export function getExplicitLocaleFromAcceptLanguage(
	acceptLanguage: string | null,
): Locale | null {
	if (!acceptLanguage) return null;

	const segments = acceptLanguage
		.split(",")
		.map((s) => {
			const [lang, q] = s.trim().split(";q=");
			return { lang: lang.trim().toLowerCase(), q: q ? Number(q) : 1 };
		})
		.sort((a, b) => b.q - a.q);

	for (const { lang } of segments) {
		// Try exact match first, then prefix match (e.g. "id-ID" → "id")
		if (isValidLocale(lang)) return lang;
		const prefix = lang.split("-")[0];
		if (isValidLocale(prefix)) return prefix;
	}

	return null;
}

/** Extract preferred locale from Accept-Language header value. */
export function getLocaleFromAcceptLanguage(
	acceptLanguage: string | null,
): Locale {
	return getExplicitLocaleFromAcceptLanguage(acceptLanguage) ?? defaultLocale;
}

/** Read locale from cookie string (raw Cookie header value). */
export function getLocaleFromCookie(
	cookieHeader: string | null,
): Locale | null {
	if (!cookieHeader) return null;
	const match = cookieHeader.match(/(?:^|;\s*)locale=([^;]+)/);
	if (match && isValidLocale(match[1])) return match[1] as Locale;
	return null;
}

/**
 * Extract the locale from a URL pathname's first segment (e.g. "/en/jobs" →
 * "en"). Falls back to `defaultLocale` when the segment is missing or not a
 * supported locale. For client components that render outside next-intl /
 * router context (error boundaries, the root not-found boundary) and
 * therefore cannot read the `[locale]` route param directly — same pattern
 * `NotFoundContent`'s `detectLocale()` uses inline.
 */
export function getLocaleFromPathname(pathname: string | null): Locale {
	if (!pathname) return defaultLocale;
	const first = pathname.split("/").filter(Boolean)[0];
	return first && isValidLocale(first) ? first : defaultLocale;
}

/**
 * `/[locale]/blog/<slug>` 308s to the post's own locale, so the middleware's
 * `/en` → `/id` Accept-Language correction must skip it: an English-only post
 * would otherwise loop (/en → /id → /en, ERR_TOO_MANY_REDIRECTS).
 */
export function isBlogPostPath(pathname: string): boolean {
	return /^\/(?:en|id)\/blog\/[^/]+/.test(pathname);
}

/**
 * `/en/collaboration/<slug>` is often English-only (no `/id` twin), so the
 * middleware's `/en` → `/id` Accept-Language correction must skip it too or the
 * visitor lands on a 404 (and the moved blog posts 308 here).
 */
export function isCollaborationPostPath(pathname: string): boolean {
	return /^\/(?:en|id)\/collaboration\/[^/]+/.test(pathname);
}
